// Piezas propias de Comercial que @iavmx/ui no trae (segmentado, barra de
// progreso, franja de etapas, bloque de IA, línea de tiempo). Candidatas a
// subir al paquete cuando otra app las necesite.
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Lock, Sparkles } from "lucide-react";
import { cn, Pill } from "@iavmx/ui";
import { fechaHora, relativo } from "./formato";
import { ETAPAS, ETIQUETA_ETAPA, type Alertas, type Etapa } from "./tipos";

/** Control segmentado: dos o tres opciones excluyentes. */
export function Segmentado<T extends string>({ valor, opciones, onCambio, etiqueta }: { valor: T; opciones: { valor: T; etiqueta: string }[]; onCambio: (v: T) => void; etiqueta: string }) {
  return (
    <div role="radiogroup" aria-label={etiqueta} className="inline-flex border border-linea-fuerte">
      {opciones.map((o) => (
        <button
          key={o.valor}
          type="button"
          role="radio"
          aria-checked={valor === o.valor}
          onClick={() => onCambio(o.valor)}
          className={cn(
            "h-8 px-3 font-ui text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            valor === o.valor ? "bg-tinta text-tinta-inversa" : "text-tinta-3 hover:bg-fondo-suave",
          )}
        >
          {o.etiqueta}
        </button>
      ))}
    </div>
  );
}

/** Barra plana de progreso (azul informativa o tinta). */
export function Barra({ pct, tono = "azul", etiqueta }: { pct: number | null; tono?: "azul" | "tinta"; etiqueta?: string }) {
  const v = Math.max(0, Math.min(100, pct ?? 0));
  return (
    <div className="relative h-2 w-full bg-fondo-alzado" role="progressbar" aria-valuenow={v} aria-valuemin={0} aria-valuemax={100} aria-label={etiqueta}>
      <div className={cn("absolute inset-y-0 left-0", tono === "azul" ? "bg-azul" : "bg-tinta")} style={{ width: `${v}%` }} />
    </div>
  );
}

/** Franja de seis etapas; la actual en tinta, las pasadas en fondo alzado. */
export function FranjaEtapas({ etapa, compacta = false }: { etapa: Etapa; compacta?: boolean }) {
  const idx = ETAPAS.indexOf(etapa);
  return (
    <ol className="flex" aria-label="Etapa del pipeline">
      {ETAPAS.map((e, i) => {
        const actual = e === etapa;
        const pasada = i < idx && etapa !== "perdido";
        return (
          <li
            key={e}
            aria-current={actual ? "step" : undefined}
            className={cn(
              "flex h-9 flex-1 items-center justify-center border border-l-0 border-linea-fuerte px-1 font-ui text-xs font-semibold first:border-l",
              actual ? "border-tinta bg-tinta text-tinta-inversa" : pasada ? "bg-fondo-alzado text-tinta-2" : "text-tinta-tenue",
            )}
          >
            <span className={cn(compacta && !actual && "sm:hidden")}>{compacta && !actual ? ETIQUETA_ETAPA[e][0] : ETIQUETA_ETAPA[e]}</span>
            {compacta && !actual && <span className="hidden sm:inline">{ETIQUETA_ETAPA[e]}</span>}
          </li>
        );
      })}
    </ol>
  );
}

export function PillEtapa({ etapa }: { etapa: Etapa }) {
  const tono = etapa === "ganado" ? "verde" : etapa === "perdido" ? "neutro" : etapa === "negociacion" || etapa === "propuesta" ? "tinta" : "neutro";
  return <Pill tono={tono}>{ETIQUETA_ETAPA[etapa]}</Pill>;
}

/** Pills de inactividad según la regla del intake: delineada = aviso, tinta = detenida / escalada. */
export function PillsAlerta({ alertas, etapa, altoValor }: { alertas: Alertas; etapa: Etapa; altoValor: boolean }) {
  if (etapa === "ganado" || etapa === "perdido") return null;
  const pills: React.ReactNode[] = [];
  if (alertas.escalar) pills.push(<Pill key="esc" tono="tinta">Alto valor · escalada a dirección</Pill>);
  else if (alertas.detenida) pills.push(<Pill key="det" tono="tinta">Detenida {alertas.diasSinActividad} días</Pill>);
  else if (alertas.diasSinActividad >= 3) pills.push(<Pill key="sin" tono="neutro">Sin actividad {alertas.diasSinActividad} días</Pill>);
  if (alertas.vencida) pills.push(<Pill key="ven" tono="rojo">Próxima acción vencida</Pill>);
  if (altoValor && !alertas.escalar) pills.push(<Pill key="alto" tono="ambar">Alto valor</Pill>);
  if (!pills.length) return null;
  return <div className="flex flex-wrap gap-1.5">{pills}</div>;
}

/** Bloque azul de lo que viene de la IA: borde izquierdo azul, kicker con chispa. */
export function BloqueIa({ titulo, children, pie }: { titulo: string; children: React.ReactNode; pie?: React.ReactNode }) {
  return (
    <div className="relative border border-linea-fuerte bg-fondo-suave p-4 pl-5 before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-azul">
      <div className="mb-2 flex items-center gap-2 font-ui text-xs font-semibold text-azul">
        <Sparkles className="size-3.5" aria-hidden />
        {titulo}
      </div>
      <div className="font-ui text-sm text-tinta-2">{children}</div>
      {pie && <div className="mt-3 font-ui text-xs text-tinta-tenue">{pie}</div>}
    </div>
  );
}

export function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("font-ui text-[11px] font-semibold uppercase tracking-[.08em] text-tinta-tenue", className)}>{children}</div>;
}

/** Cifra grande con leyenda: los números van en Bitter. */
export function Cifra({ valor, leyenda, tamano = "md" }: { valor: React.ReactNode; leyenda: React.ReactNode; tamano?: "md" | "lg" }) {
  return (
    <div>
      <div className={cn("font-titulo font-bold tabular-nums leading-none", tamano === "lg" ? "text-3xl" : "text-2xl")}>{valor}</div>
      <div className="mt-1 font-ui text-xs text-tinta-tenue">{leyenda}</div>
    </div>
  );
}

export function Candado({ texto }: { texto: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-ui text-xs text-tinta-tenue">
      <Lock className="size-3.5" aria-hidden />
      {texto}
    </span>
  );
}

/** Fila de línea de tiempo: punto, título + detalle, fecha a la derecha. */
export function FilaTiempo({ titulo, detalle, en, azul = false }: { titulo: React.ReactNode; detalle?: React.ReactNode; en: string | null; azul?: boolean }) {
  return (
    <li className="grid grid-cols-[16px_minmax(0,1fr)_auto] gap-3 border-b border-linea py-3 last:border-b-0">
      <span className={cn("mt-1.5 size-2 rounded-full", azul ? "bg-azul" : "bg-tinta")} aria-hidden />
      <div className="min-w-0">
        <div className="font-ui text-sm font-semibold text-tinta">{titulo}</div>
        {detalle && <div className="font-ui text-xs text-tinta-3">{detalle}</div>}
      </div>
      <time className="whitespace-nowrap font-ui text-xs text-tinta-tenue" dateTime={en ?? undefined} title={en ? fechaHora(en) : undefined}>
        {en ? relativo(en) : ""}
      </time>
    </li>
  );
}

export function Miga({ partes }: { partes: { texto: string; to?: string; params?: Record<string, string> }[] }) {
  return (
    <nav aria-label="Ubicación" className="mb-2 font-ui text-xs text-tinta-tenue">
      {partes.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden> › </span>}
          {p.to ? (
            <Link to={p.to} params={p.params} className="text-azul hover:underline">
              {p.texto}
            </Link>
          ) : (
            <span>{p.texto}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

/** Aviso en línea de una acción que salió mal (no tumba la pantalla). */
export function ErrorEnLinea({ error }: { error: unknown }) {
  if (!error) return null;
  const m = error instanceof Error ? error.message : String(error);
  return (
    <p role="alert" className="border border-rojo/40 bg-rojo/5 px-3 py-2 font-ui text-sm text-rojo">
      {m}
    </p>
  );
}

/** Sección con encabezado pequeño en versalitas. */
export function Seccion({ titulo, accion, children, className }: { titulo: string; accion?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn("space-y-3", className)}>
      <div className="flex items-baseline justify-between gap-3 border-b border-linea pb-2">
        <h2 className="font-titulo text-base font-bold">{titulo}</h2>
        {accion}
      </div>
      {children}
    </section>
  );
}
